import { Link } from "react-router-dom";
import { FileText, Calendar } from "lucide-react";
import PageMeta from "./PageMeta";

// The current agenda for the next upcoming meeting.
const currentAgenda = {
  label: "November 2026 Meeting Agenda",
  date: "2 November 2026",
  file: "/assets/Agenda_for_RSAA_Nov_2026_Meeting.pdf",
};

// The most recent set of meeting minutes.
const currentMinutes = {
  label: "October 2026 Meeting",
  date: "5 October 2026",
  file: "/assets/Minutes_October_2026_meeting.pdf",
};

// The current treasurer's report.
const currentReport = {
  label: "Treasurer's Report – November 2026",
  date: "28 October 2026",
  file: "/assets/Treasurers_Report_28_10_2026.pdf",
};

function DocumentCard({ item, colorClass, borderHover, bgHover, badgeBorder, badgeBg, badgeText, icon: Icon }) {
  return (
    <a
      href={item.file}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-5 rounded-2xl border border-white/10 bg-white/5 p-5 transition ${borderHover} ${bgHover}`}
    >
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${colorClass}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-white">{item.label}</p>
        <p className="mt-0.5 text-xs text-slate-400">{item.date}</p>
      </div>
      <span className={`shrink-0 rounded-lg border ${badgeBorder} ${badgeBg} px-3 py-1 text-xs font-medium ${badgeText}`}>
        Open PDF
      </span>
    </a>
  );
}

export default function UpcomingMeetingPage() {
  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta title="Latest Meeting Agenda & Minutes | Redruth Sea Angling Association" noindex />
      <div className="mx-auto max-w-4xl px-6 py-16 sm:px-8 lg:px-12">

        <Link to="/" className="standalone-only inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-400/20">
          ← Back
        </Link>

        <div className="mt-8">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Redruth Sea Angling Association</p>
          <h1 className="heading-glow mt-3 text-4xl font-bold tracking-tight text-white sm:text-5xl">Latest Meeting Agenda & Minutes</h1>
          <p className="mt-4 text-base leading-7 text-slate-400">
            Current agenda for the next meeting and minutes from the previous meeting.
          </p>
        </div>

        {/* Current Agenda */}
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="h-5 w-5 text-amber-300" />
            <h2 className="text-xl font-semibold text-amber-300">Meeting Agenda</h2>
          </div>
          {currentAgenda ? (
            <DocumentCard
              item={currentAgenda}
              icon={FileText}
              colorClass="bg-amber-400/10 text-amber-300"
              borderHover="hover:border-amber-300/30"
              bgHover="hover:bg-amber-400/5"
              badgeBorder="border-amber-300/20"
              badgeBg="bg-amber-400/10"
              badgeText="text-amber-300"
            />
          ) : (
            <p className="text-slate-400">No agenda has been published yet.</p>
          )}
        </div>

        {/* Previous Minutes */}
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-5 w-5 text-cyan-300" />
            <h2 className="text-xl font-semibold text-cyan-300">Latest Meeting Minutes</h2>
          </div>
          {currentMinutes ? (
            <DocumentCard
              item={currentMinutes}
              icon={FileText}
              colorClass="bg-cyan-400/10 text-cyan-300"
              borderHover="hover:border-cyan-300/30"
              bgHover="hover:bg-cyan-400/5"
              badgeBorder="border-cyan-300/20"
              badgeBg="bg-cyan-400/10"
              badgeText="text-cyan-300"
            />
          ) : (
            <p className="text-slate-400">No minutes have been published yet.</p>
          )}
        </div>

        {/* Treasurer's Report */}
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-5 w-5 text-emerald-300" />
            <h2 className="text-xl font-semibold text-emerald-300">Treasurer's Report</h2>
          </div>
          {currentReport ? (
            <DocumentCard
              item={currentReport}
              icon={FileText}
              colorClass="bg-emerald-400/10 text-emerald-300"
              borderHover="hover:border-emerald-300/30"
              bgHover="hover:bg-emerald-400/5"
              badgeBorder="border-emerald-300/20"
              badgeBg="bg-emerald-400/10"
              badgeText="text-emerald-300"
            />
          ) : (
            <p className="text-slate-400">No report has been published yet.</p>
          )}
        </div>

        <p className="mt-12 text-center text-xs text-slate-500">
          For a full archive of past meetings, visit the <Link to="/members" className="text-cyan-400 hover:text-cyan-300 underline">Members' Documents</Link> section.
        </p>

      </div>
    </div>
  );
}
